import { XYLocation } from "@nens/deck-gl";

import { AppState } from "../App";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";

export type PolygonStatus = "SELECT_OR_NEW" | "DRAWING_MODE" | "NEED_CONFIRMATION";

export type EditableRaster = "DEM" | "INTERCEPTION" | "INFILTRATION";
export type AbsoluteOrRelative = "Absolute" | "Relative";
export type MaybePoints = null | XYLocation[];

export type NewValue = string;

interface PolygonEdit {
  status: PolygonStatus;
  points: MaybePoints;
  newValue: NewValue;
  absoluteOrRelative: AbsoluteOrRelative;
  editableRaster: EditableRaster;
}

const INITIAL_STATE: PolygonEdit = {
  status: "SELECT_OR_NEW",
  points: null,
  newValue: "0",
  absoluteOrRelative: "Absolute",
  editableRaster: "DEM",
};

const SET_STATUS = "polygonEdit/SET_STATUS";
const SET_POINTS = "polygonEdit/SET_POINTS";
const SET_NEW_VALUE = "polygonEdit/SET_NEW_VALUE";
const SET_EDITABLE_RASTER = "polygonEdit/SET_EDITABLE_RASTER";
const SET_ABSOLUTE_OR_RELATIVE = "polygonEdit/SET_ABSOLUTE_OR_RELATIVE";

export const getStatus = (state: AppState) => state.polygonEdit.status;
export const getPoints = (state: AppState) => state.polygonEdit.points;
export const getNewValue = (state: AppState) => state.polygonEdit.newValue;
export const getAbsoluteOrRelative = (state: AppState) => state.polygonEdit.absoluteOrRelative;
export const getEditableRaster = (state: AppState) => state.polygonEdit.editableRaster;

interface SetStatus {
  type: typeof SET_STATUS;
  status: PolygonStatus;
}
interface SetPoints {
  type: typeof SET_POINTS;
  points: MaybePoints;
}
interface SetNewValue {
  type: typeof SET_NEW_VALUE;
  newValue: NewValue;
}
interface SetEditableRaster {
  type: typeof SET_EDITABLE_RASTER;
  editableRaster: EditableRaster;
}
interface SetAbsoluteOrRelative {
  type: typeof SET_ABSOLUTE_OR_RELATIVE;
  absoluteOrRelative: AbsoluteOrRelative;
}

type PolygonEditAction =
  | SetStatus
  | SetPoints
  | SetNewValue
  | SetEditableRaster
  | BackToStartScreen
  | SetAbsoluteOrRelative;

export const setStatusAction = (status: PolygonStatus): PolygonEditAction => {
  return {
    type: SET_STATUS,
    status: status,
  };
};
export const setPointsAction = (points: MaybePoints): PolygonEditAction => {
  return {
    type: SET_POINTS,
    points: points,
  };
};
export const setNewValueAction = (newValue: NewValue): PolygonEditAction => {
  return {
    type: SET_NEW_VALUE,
    newValue: newValue,
  };
};
export const setEditableRasterAction = (editableRaster: EditableRaster): PolygonEditAction => {
  return {
    type: SET_EDITABLE_RASTER,
    editableRaster: editableRaster,
  };
};

export const setAbsoluteOrRelativeAction = (absoluteOrRelative: AbsoluteOrRelative) => {
  return {
    type: SET_ABSOLUTE_OR_RELATIVE,
    absoluteOrRelative: absoluteOrRelative,
  };
};

export default function reducer(state = INITIAL_STATE, action: PolygonEditAction) {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SET_STATUS: {
      const { status } = action;
      if (status !== state.status) {
        return {
          ...state,
          status: status,
          points: status === "SELECT_OR_NEW" ? null : state.points,
        };
      } else {
        return state;
      }
    }
    case SET_POINTS: {
      const { points } = action;
      return {
        ...state,
        points,
      };
    }
    case SET_NEW_VALUE: {
      const { newValue } = action;
      return {
        ...state,
        newValue,
      };
    }
    case SET_ABSOLUTE_OR_RELATIVE: {
      const { absoluteOrRelative } = action;
      return {
        ...state,
        absoluteOrRelative,
      };
    }
    case SET_EDITABLE_RASTER: {
      const { editableRaster } = action;
      return {
        ...state,
        editableRaster,
      };
    }
    default:
      return state;
  }
}
